import React from 'react';
import FormView from '../../components/views/formView';
import { Grid, Box, FormControlLabel, Switch } from '@mui/material';
import FieldText from '../../components/general/fields/fieldText';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { USEREDIT, USERLIST } from '../../../routes/paths';
import FieldSelect from '../../components/general/fields/fieldSelect';
import { RootState } from '../../../redux/store';
import { SwalCustomClass } from '../../components/general/toastSwals/swalCustomClass';
import { UsersMessages } from '../../../shared/messages/usersMessages';
import {
    asyncPatchtUser,
    asyncPostUser,
} from '../../../services/users.service';
import { useAuthContext } from '../../../contexts/authContext';
import MultipleAutocompleteField from '../../components/general/fields/multipleAutocompleteField';
import {
    ProjectInterface,
    ProjectsList,
} from '../../../shared/types/project.interface';

type Props = {
    idUser?: any;
    user?: any;
    onSave?: any;
    projects?: ProjectsList[];
};

const UserForm = ({ user, idUser, onSave, projects }: Props) => {
    const navigate = useNavigate();
    const { modeTheme } = useAuthContext();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { id: idUserLog } = logUser || {};
    const { usersMessages }: any = UsersMessages;

    const formik = useFormik({
        initialValues: {
            code: user !== undefined ? user.code : '',
            name: user !== undefined ? user.name : '',
            surname: user !== undefined ? user.surname : '',
            username: user !== undefined ? user.username : '',
            password: '',
            password_retry: '',
            type: user !== undefined ? user.type : '',
            projects: user !== undefined ? user.projectIds : [],
            status: user !== undefined ? user.status : '',
        },
        validationSchema: Yup.object({
            code: Yup.number()
                .typeError('El código debe ser un número')
                .required('El código es obligatorio'),
            name: Yup.string().required('El nombre es obligatorio'),
            surname: Yup.string().required('El apellido es obligatorio'),
            username: Yup.string()
                .email('El correo es invalido')
                .required('El correo de usuario es obligatorio')
                .test('Validate Email', 'El correo es inválido', (value) => {
                    const re =
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return re.test(String(value).toLowerCase());
                }),
            password: Yup.string().when({
                is: () => Boolean(idUser === undefined),
                then: (schema) =>
                    schema
                        .required('La contraseña es obligatoria')
                        .min(
                            8,
                            'La contraseña debe tener al menos 8 caracteres'
                        ),
            }),
            password_retry: Yup.string().when(['password'], {
                is: (password: any) =>
                    Boolean(idUser === undefined) || password !== undefined,
                then: (schema) =>
                    schema
                        .required(
                            'La confirmación de la contraseña es obligatoria'
                        )
                        .oneOf(
                            [Yup.ref('password')],
                            'Las contraseñas no coinciden'
                        ),
            }),
            type: Yup.string().required('El perfil es obligatorio'),
            projects: Yup.array()
                .required('El campo proyecto es requerido')
                .min(1, 'Debes seleccionar al menos una proyecto'),
        }),
        onSubmit: async (formUser) => {
            formUser.projects = formUser.projects.map(
                (item: ProjectInterface) => item.id
            );
            if (idUser !== undefined) {
                asyncPatchtUser(idUser, formUser).then((body: any) => {
                    if (body?.items !== undefined) {
                        onSave(true);
                        navigate(`${USEREDIT}/${body.items.id}`);
                        SwalCustomClass.fire({
                            text: usersMessages.MESSAGE_EDIT,
                            icon: 'success',
                        });
                    } else if (body?.errors !== undefined) {
                        SwalCustomClass.fire({
                            text: usersMessages[body.message],
                            icon: 'error',
                        });
                    }
                });
            } else {
                const body: any = await asyncPostUser(formUser);
                if (body?.items !== undefined) {
                    navigate(`${USEREDIT}/${body.items.id}`);
                    SwalCustomClass.fire({
                        text: usersMessages.MESSAGE_NEW,
                        icon: 'success',
                    });
                } else if (body?.errors !== undefined) {
                    SwalCustomClass.fire({
                        text: usersMessages[body.message],
                        icon: 'error',
                    });
                }
            }
        },
    });

    const profiles = [
        {
            id: 1,
            name: 'admin',
        },
        {
            id: 3,
            name: 'user',
        },
        {
            id: 4,
            name: 'reporter',
        },
    ];

    return (
        <>
            <FormView
                title={
                    idUser !== undefined
                        ? usersMessages.TITLE_EDIT
                        : usersMessages.TITLE_NEW
                }
                onClickSave={formik.handleSubmit}
                onClickBack={() => navigate(USERLIST)}
            />
            <Box mt={3} px={2}>
                <Grid
                    container
                    spacing={{ xs: 1.5, md: 1.5 }}
                    columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
                >
                    {idUser && !user.status && (
                        <Grid item xs={12}>
                            <FormControlLabel
                                onClick={formik.handleChange}
                                value={formik.values.status}
                                checked={formik.values.status}
                                control={<Switch color="default" />}
                                label="Estado"
                                name="status"
                                labelPlacement="end"
                                slotProps={{
                                    typography: {
                                        color:
                                            modeTheme === 'darkMode'
                                                ? 'primary.contrastText'
                                                : 'primary.main',
                                    },
                                }}
                            />
                        </Grid>
                    )}
                    <Grid
                        item
                        xs={12}
                        md={idUserLog === idUser ? 1.9 : 2}
                        lg={idUserLog === idUser ? 1.9 : 2}
                    >
                        <FieldText
                            label="Código"
                            name="code"
                            size="small"
                            required={true}
                            touched={formik.touched.code}
                            errors={formik.errors.code}
                            value={formik.values.code}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={idUserLog === idUser ? 3.3 : 3.5}
                        lg={idUserLog === idUser ? 3.3 : 3.5}
                    >
                        <FieldText
                            label="Nombre"
                            name="name"
                            size="small"
                            required={true}
                            touched={formik.touched.name}
                            errors={formik.errors.name}
                            value={formik.values.name}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={idUserLog === idUser ? 3.3 : 3.5}
                        lg={idUserLog === idUser ? 3.3 : 3.5}
                    >
                        <FieldText
                            label="Apellido"
                            name="surname"
                            size="small"
                            required={true}
                            touched={formik.touched.surname}
                            errors={formik.errors.surname}
                            value={formik.values.surname}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    {idUserLog !== idUser ? (
                        <Grid item xs={12} md={3} lg={3}>
                            <FieldSelect
                                data={profiles}
                                label="Pérfil"
                                name="type"
                                size="small"
                                required={true}
                                defineValue="value"
                                touched={formik.touched.type}
                                errors={formik.errors.type}
                                value={formik.values.type}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                    ) : null}
                    <Grid item xs={12} md={3} lg={3}>
                        <MultipleAutocompleteField
                            data={projects}
                            label="proyecto"
                            name="projects"
                            size="small"
                            required={true}
                            touched={formik.touched.projects}
                            errors={formik.errors.projects as React.ReactNode}
                            value={formik.values.projects}
                            onChangeValue={(value) =>
                                formik.setFieldValue('projects', value)
                            }
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={idUserLog === idUser ? 3.5 : 3}
                        lg={idUserLog === idUser ? 3.5 : 3}
                    >
                        <FieldText
                            label="Correo Usuario"
                            name="username"
                            size="small"
                            required={true}
                            touched={formik.touched.username}
                            errors={formik.errors.username}
                            value={formik.values.username}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={3} lg={3}>
                        <FieldText
                            label="Contraseña"
                            name="password"
                            type="password"
                            size="small"
                            required={true}
                            touched={formik.touched.password}
                            errors={formik.errors.password}
                            value={formik.values.password}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={3} lg={3}>
                        <FieldText
                            label="Confirmar Contraseña"
                            name="password_retry"
                            type="password"
                            size="small"
                            required={true}
                            touched={formik.touched.password_retry}
                            errors={formik.errors.password_retry}
                            value={formik.values.password_retry}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default UserForm;
